<template>
  <div v-if="canSwitchScenes && sceneTree.activeScene && (!singleScene || mode === 'edit')" class="scene-switcher" ref="sceneSwitcherContainer" @contextmenu.prevent="showSwitcherContextMenu">
    <button
      ref="sceneSwitcherButton"
      type="button"
      class="button-icon ph-1 scene-switcher__button"
      @click="toggleSwitcherPanel"
    >
      <lucide-icon name="file" />
      <div v-if="presence.footerSceneTitleVisible || (!singleScene && presence.footerSceneTitleVisible !== false)" class="scene-switcher__buttontext">
        <span v-if="sceneTree.activeScene.title">
          {{ sceneTree.activeScene.title }}
        </span>
        <span v-else class="placeholder-text" style="opacity: 1;">
          {{ $t("objectNames.scene") }}
        </span>
      </div>
    </button>

    <floating-element
      class="scene-switcher__panel"
      :visible="switcherPanel.visible"
      :x="switcherPanel.x"
      :y="switcherPanel.y"
      :width="switcherPanel.width"
      :min-width="60"
      :min-height="40"
      transition-name="transition-pop-in-up"
      x-alignment="center"
      y-alignment="bottom"
      :non-closing-elements="sceneSwitcherButton ? [sceneSwitcherButton] : []"
      @click:outside="onClickOutsideSwitcherPanel"
    >
      <template #default>
        <div class="scene-switcher__content">
          <template v-if="props.mode === 'edit'">
            <div class="button-transparent scene-switcher__list-item">
              <button
                class="button-transparent scene-switcher__list-item-button"
                type="button"
                @click="createNewScene"
              >
                <LucideIcon name="plus" /> {{ $t("actions.createScene") }}
              </button>
            </div>
            <hr class="mv-1" />
          </template>
          <SortableList
            :items="scenes"
            item-key="id"
            :order="sceneStore.order"
            :selected="sceneTree.activeScene ? [sceneTree.activeScene.id] : []" 
            :gap="'0'"
            :sortable="props.mode === 'edit'"
            line-class="scene-switcher__list-item"
            @update:order="sceneStore.saveOrder($event)"
          >
            <template #default="{ item: scene }">
              <template v-if="editingTitleSceneId != scene.id">
                <button
                  class="button-transparent scene-switcher__list-item-button"
                  type="button"
                  @click="switchActiveScene(scene.id)"
                  @dblclick="startEditingSceneTitle(scene)"
                >
                  <span v-if="scene.title">
                    {{ scene.title }}
                  </span>
                  <span v-else class="placeholder-text" style="opacity: 1;">
                    {{ $t("objectNames.scene") + " " + (scene.id + 1) }}
                  </span>
                </button>
                <template v-if="props.mode === 'edit'">
                  <button class="button-icon scene-switcher__action-icon" @click="startEditingSceneTitle(scene)">
                    <lucide-icon name="pencil" />
                  </button>
                  <button class="button-icon scene-switcher__action-icon" :data-scene-id="scene.id" @click="showContextMenu($event, scene.id)">
                    <lucide-icon name="ellipsis" />
                  </button>
                </template>
              </template>
              <template v-else>
                <input
                  ref="sceneTitleInput"
                  type="text"
                  class="button-transparent scene-switcher__list-item-button"
                  v-model="editedSceneTitle"
                  @keyup.enter="saveSceneTitle"
                  @blur="saveSceneTitle"
                  @keyup.esc="cancelEditingSceneTitle"
                />
                <button class="button-icon scene-switcher__action-icon" @click="saveSceneTitle">
                  <lucide-icon name="check" />
                </button>
                <button class="button-icon scene-switcher__action-icon" @click="cancelEditingSceneTitle">
                  <lucide-icon name="x" />
                </button>
              </template>
            </template>
          </SortableList>
        </div>
      </template>
    </floating-element>

    <context-menu
      v-if="sceneContextMenu.visible && sceneContextMenu.sceneId != null"
      :x="sceneContextMenu.x"
      :y="sceneContextMenu.y"
      x-alignment="right"
      y-alignment="bottom"
      transition="transition-from-bottom-right-corner"
      @close="closeSceneContextMenu($event)"
    >
      <!-- <button
        type="button"
        class="context-menu__entry gap-2"
        style="display: flex"
        @click.prevent="toggleEditModeOnly(sceneContextMenu.sceneId)"
      >
        <lucide-icon v-if="contextScene?.editModeOnly" name="check" class="self-center" />
        <lucide-icon v-else name="square" style="opacity: 0;" />
        <div>
          {{ $t("labels.sceneInEditModeOnly") }}
        </div>
      </button> -->
      <button
        type="button"
        class="context-menu__entry"
        @click="duplicateScene(sceneContextMenu.sceneId)"
      >
        {{ $t("actions.duplicateScene") }}
      </button>
      <button
        v-if="canDeleteScene"
        type="button"
        class="context-menu__entry"
        @click="deleteScene(sceneContextMenu.sceneId)"
      >
        {{ $t("actions.deleteScene") }}
      </button>
    </context-menu>

    <context-menu
      v-if="sceneSwitcherContextMenu.visible"
      :x="sceneSwitcherContextMenu.x"
      :y="sceneSwitcherContextMenu.y"
      x-alignment="right"
      y-alignment="bottom"
      transition="transition-from-bottom-right-corner"
      @close="sceneSwitcherContextMenu.visible = false"
    >
      <button
        v-if="presence.footerSceneTitleVisible"
        type="button"
        class="context-menu__entry"
        @click="hideSceneTitle()"
      >
        {{ $t("actions.hideSceneTitle") }}
      </button>
      <button
        v-else
        type="button"
        class="context-menu__entry"
        @click="showSceneTitle()"
      >
        {{ $t("actions.showSceneTitle") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { reactive, useTemplateRef, computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@core/ui/LucideIcon.vue';
import FloatingElement from '@core/ui/FloatingElement.vue';
import SortableList from '@core/ui/SortableList.vue';
import ContextMenu from '@core/ui/ContextMenu.vue';
// import { showError } from '@core/ui/alert/alert';
import { useSceneStore } from './scenes.store';
import { useSceneTree } from './sceneTree';
import { usePresenceStore } from '@editor/store/presence.store';
import { useLoadedModel } from '@editor/store/loadedModel.store';
import { useLicense } from '@/api/license.store';
import type { SceneItem } from './SceneItem';

const props = defineProps<{
  mode: "edit" | string;
}>();

const sceneSwitcherButton = useTemplateRef('sceneSwitcherButton');

const { t } = useI18n();
const sceneStore = useSceneStore();
const sceneTree = useSceneTree();
const presence = usePresenceStore();

const scenes = computed(() => {
  if (props.mode === 'edit') {
    return sceneStore.orderedItems;
  }
  return sceneStore.orderedItems.filter(scene => !scene.editModeOnly);
});

const singleScene = computed(() => scenes.value.length <= 1);

// ======================
// PERMISSIONS

const loadedModelStore = useLoadedModel();
const license = useLicense();

const canSwitchScenes = computed(() => {
  const loadedModel = loadedModelStore.loadedModel;
  if (loadedModel == null) return false;
  if (loadedModel.type == "workspace_model") {
    // If we have direct access, check our own license for permissions.
    console.log("license", license);
    return license.limits.canSwitchScenes ?? false;
  }
  if (loadedModel.type == "shared_model") {
    // For shared models, we also check our own license, because the model permissions are not relevant if our license does not allow switching scenes at all.
    console.log("license", license);
    if (!license.limits.canSwitchScenes) {
      return false;
    }
  }
  // Otherwise, our own permission do not count. Check the permissions of the loaded model.
  const permissions = { ...loadedModel.permissions };
  console.log(loadedModel.type, permissions);
  return permissions.canSwitchScenes ?? false;
});

// ======================
// TITLE EDITING

const sceneTitleInput = useTemplateRef('sceneTitleInput');
const editingTitleSceneId = ref<number | null>(null);
const editedSceneTitle = ref("");

function startEditingSceneTitle(scene: SceneItem) {
  if (props.mode !== 'edit') return;
  editingTitleSceneId.value = scene.id;
  editedSceneTitle.value = scene.title;
  nextTick(() => {
    const input = sceneTitleInput.value;
    input?.select();
  });
}

function saveSceneTitle() {
  if (props.mode !== 'edit') return;
  if (editingTitleSceneId.value === null) return;
  const scene = sceneStore.items[editingTitleSceneId.value];
  if (!scene) return;
  sceneStore.update({
    id: scene.id,
    title: editedSceneTitle.value
  });
  editingTitleSceneId.value = null;
}

function cancelEditingSceneTitle() {
  editingTitleSceneId.value = null;
}

// =======================
// SCENE SELECTION

function switchActiveScene(sceneId: number) {
  presence.set("activeSceneId", sceneId);
  var activeLayer = presence.activeSceneLayers?.[sceneId];
  if (activeLayer == null) {
    const sceneLayers = sceneTree.layerNodes.filter(layer => layer.node.data.layerType == "scene");
    if (sceneLayers.length > 0) {
      const orderedLayers = sceneLayers.sort((a, b) => b.order - a.order);
      const topLayer = orderedLayers[0];
      presence.setActiveLayer(topLayer.uid);
    }
  }
}

// =======================
// SWITCHER PANEL

const switcherPanel = reactive({
  visible: false,
  x: 100,
  y: 100,
  width: 250,
  height: 40
});

function toggleSwitcherPanel() {
  switcherPanel.visible = !switcherPanel.visible;
  if (switcherPanel.visible) {
    // Position the panel below the button
    const buttonRect = sceneSwitcherButton.value?.getBoundingClientRect();
    if (buttonRect) {
      switcherPanel.x = buttonRect.left + buttonRect.width / 2;
      switcherPanel.y = buttonRect.top; // 5px gap
    }
  }
}

function onClickOutsideSwitcherPanel(event: MouseEvent) {
  const path = event.composedPath();
  // Prevent drag events from closing the panel 
  if (path.length > 0 && (path[0] as HTMLElement).id == "app") {
    return;
  }
  switcherPanel.visible = false;
}

// =======================
// SCENE CONTEXT MENU

const sceneContextMenu = reactive({
  x: 0 as number,
  y: 0 as number,
  sceneId: null as number | null,
  visible: false
});

// const contextScene = computed(() => {
//   if (sceneContextMenu.sceneId == null) return null;
//   return sceneStore.items[sceneContextMenu.sceneId] || null;
// });

function showContextMenu(mouseEvent: MouseEvent, sceneId: number) {
  if (sceneContextMenu.visible && sceneContextMenu.sceneId === sceneId) {
    sceneContextMenu.visible = false;
    return;
  } 
  const rect = (mouseEvent.target as HTMLElement).getBoundingClientRect();
  sceneContextMenu.x = rect.right;
  sceneContextMenu.y = rect.top;
  sceneContextMenu.sceneId = sceneId;
  sceneContextMenu.visible = true;
}

function closeSceneContextMenu(event: MouseEvent) {
  const currentSceneId = "" + sceneContextMenu.sceneId;
  const path = event.composedPath();
  // prevent ellipsis button click from closing the menu
  if (path.some(el => (el as HTMLElement).dataset?.sceneId == currentSceneId)) {
    return;
  }
  sceneContextMenu.sceneId = null;
  sceneContextMenu.visible = false;
}

// =======================
// SCENE ACTIONS

async function createNewScene() {
  const newScene = await sceneStore.createWithDefaultLayers({
    title: t("objectNames.scene") + " " + (sceneStore.orderedItems.length + 1)
  });
  switchActiveScene(newScene.id);
  startEditingSceneTitle(newScene);
}

async function duplicateScene(sceneId: number | null) {
  if (sceneId == null) return;
  const scene = sceneStore.items[sceneId];
  if (!scene) return;
  const newScene = await sceneStore.duplicate(sceneId, {
    title: scene.title + t("labels.copySuffix")
  });
  switchActiveScene(newScene.id);
  startEditingSceneTitle(newScene);
}

const canDeleteScene = computed(() => {
  return sceneStore.orderedItems.length > 1;
});

function deleteScene(sceneId: number | null) {
  if (sceneId == null) return;
  sceneStore.delete({ id: sceneId });
  sceneContextMenu.visible = false;
}

// function toggleEditModeOnly(sceneId: number | null) {
//   if (sceneId == null) return;
//   const scene = sceneStore.items[sceneId];
//   if (!scene) return;
//   const newValue = !scene.editModeOnly;
//   if (newValue == true) {
//     const availableScenes = sceneStore.orderedItems.filter(s => !s.editModeOnly);
//     if (availableScenes.length <= 1) {
//       showError(t("errors.oneNonEditSceneRequired"));
//       return;
//     }
//   }
//   sceneStore.update({
//     id: sceneId,
//     editModeOnly: newValue
//   });
// }

// =======================
// SWITCHER BUTTON CONTEXT MENU

const sceneSwitcherContextMenu = reactive({
  x: 0 as number,
  y: 0 as number,
  visible: false
});

function showSwitcherContextMenu(mouseEvent: MouseEvent) {
  const rect = (mouseEvent.target as HTMLElement).getBoundingClientRect();
  sceneSwitcherContextMenu.x = rect.right;
  sceneSwitcherContextMenu.y = rect.top;
  sceneSwitcherContextMenu.visible = true;
}

function hideSceneTitle() {
  presence.set("footerSceneTitleVisible", false);
}

function showSceneTitle() {
  presence.set("footerSceneTitleVisible", true);
}
</script>

<style lang="scss">
.scene-switcher {
  &__button {
    flex-direction: row;
    white-space: nowrap;
    gap: 0.5rem;
  }
  &__buttontext {
    font-size: 1rem;
    font-weight: 400;
    overflow: hidden;
    max-width: 12rem;
    text-overflow: ellipsis;
  }

  &__panel {
    padding: 0.5rem 0;
    padding-top: 0.25rem;
    align-items: center;
    overflow: hidden;
    background-color: var(--color-background-deep);
    border-radius: 0.5rem;
    box-shadow: 0px 1px 7px var(--color-shadow-3);
  }

  &__list-item {
    background-color: var(--color-background-deep);
    display: flex;
    width: 100%;
    text-align: left;

    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2em;

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible,
    &:not(:disabled):focus-within {
      background-color: var(--color-background);
    }
    &:disabled {
      color: var(--color-text-lighter);
    }
    &.-selected {   
      background-color: var(--color-primary-light);
      font-weight: 600;
      color: var(--color-text);

      &:hover {
        background-color: var(--color-primary-light);
      }
    }
  }
  &__list-item-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.25rem 1rem;
  }

  &__action-icon {
    opacity: 0;
    font-size: 1.1rem;
    pointer-events: auto;
    :hover > &,
    :focus-within > & {
      opacity: 1;
    }

    .lucide-icon {
      pointer-events: none;
    }
  }
}
</style>